import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  HowToReg as HowToRegIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  CheckCircle as CheckCircleIcon,
  FormatQuote as FormatQuoteIcon,
  EmojiObjects as EmojiObjectsIcon,
  CalendarMonth as CalendarMonthIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
// NOTE: api currently lives under super-picker but is treated as the shared
// API module for all super-* apps. If a true shared API surface is created
// later, swap this import.
import {
  api,
  RegistrationEventSummary,
  TrainingECBInviteSummary,
} from '../../super-picker/services/api';
import { logConfirmTrainingRegistrationClick } from '../../super-picker/services/analytics';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TRAINING_GREEN = '#2E7D32';
const TRAINING_GREEN_HOVER = '#1B5E20';
const REGISTERED_GREY = '#9E9E9E';
const PAST_EVENT_RED = '#C62828';
const PAST_EVENT_RED_HOVER = '#8E0000';
const PROJECT_CHIP_BLUE = '#1976D2';
const RECRUITMENT_CATEGORY = 'Recruitment';

/**
 * Huttons `Event.CategoryId` values that support a separate clock-out
 * scan. Keep in sync with the admin-side constant of the same name in
 * `ManageEventsDialog.tsx` — the agent-side dialog uses this purely as
 * a UI hint for the "Mark attendance" → "Clock out" label swap. The
 * actual clock-in / clock-out decision is server-side, derived from the
 * QR token claim.
 */
const CLOCK_OUT_CATEGORY_IDS = new Set(['C17041', 'C17042']);

function isClockOutEligibleEvent(
  event: { categoryId?: string | null } | null | undefined,
): boolean {
  const id = event?.categoryId;
  return Boolean(id && CLOCK_OUT_CATEGORY_IDS.has(id));
}
const CONFETTI_COLORS = ['#ed8936', '#f6e05e', '#48bb78', '#4299e1', '#ed64a6', '#9f7aea', '#fc8181'];

type TrainingReviewSummary = NonNullable<RegistrationEventSummary['reviews']>[number];

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
}

function TrainingReviewCelebrationOverlay({ onDone }: { onDone: () => void }) {
  const [pieces] = useState<ConfettiPiece[]>(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      delay: Math.random() * 0.8,
      duration: 1.5 + Math.random() * 1.5,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360,
    })),
  );

  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        overflow: 'hidden',
        bgcolor: 'rgba(0, 0, 0, 0.75)',
        animation: 'backdropFadeIn 0.3s ease-out forwards',
        '@keyframes backdropFadeIn': {
          '0%': { backgroundColor: 'rgba(0, 0, 0, 0)' },
          '100%': { backgroundColor: 'rgba(0, 0, 0, 0.75)' },
        },
      }}
    >
      {pieces.map((p) => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            top: -20,
            left: `${p.x}%`,
            width: p.size,
            height: p.size * 0.6,
            bgcolor: p.color,
            borderRadius: '2px',
            animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${p.rotation}deg)`,
            '@keyframes confettiFall': {
              '0%': { top: '-20px', opacity: 1, transform: `rotate(${p.rotation}deg)` },
              '100%': { top: '110vh', opacity: 0, transform: `rotate(${p.rotation + 720}deg)` },
            },
          }}
        />
      ))}
      <Box
        onClick={onDone}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          pointerEvents: 'auto',
          cursor: 'pointer',
          animation: 'centerFadeIn 0.6s ease-out forwards',
          '@keyframes centerFadeIn': {
            '0%': { opacity: 0, transform: 'translate(-50%, -50%) scale(0.8)' },
            '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          },
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, sm: 28 }, color: '#fff', textAlign: 'center', textShadow: '0 2px 16px rgba(0,0,0,0.5)', letterSpacing: 1 }}>
          Thank you!
        </Typography>
        <Typography sx={{ fontWeight: 600, fontSize: { xs: 14, sm: 16 }, color: 'rgba(255,255,255,0.85)', textAlign: 'center', textShadow: '0 1px 8px rgba(0,0,0,0.4)', mb: 1 }}>
          Thank you for sharing your training review
        </Typography>
        <Box
          sx={{
            animation: 'coinSpin 2s ease-out forwards',
            '@keyframes coinSpin': {
              '0%': { transform: 'scale(0.3) rotateY(0deg)', opacity: 0 },
              '30%': { transform: 'scale(1.2) rotateY(540deg)', opacity: 1 },
              '50%': { transform: 'scale(1) rotateY(720deg)', opacity: 1 },
              '100%': { transform: 'scale(1) rotateY(1080deg)', opacity: 1 },
            },
          }}
        >
          <Box sx={{ width: 140, height: 140, borderRadius: '50%', bgcolor: '#ed8936', color: '#041D40', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 26, filter: 'drop-shadow(0 0 24px rgba(237,137,54,0.6))' }}>
            Thank You
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface EcbInviteRow {
  name: string;
  agency: string;
  mobile: string;
}

function formatCategoryLabel(value: string): string {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeEventDescriptionHtml(html: string): string {
  return html.replace(
    /(src\s*=\s*["'])\/uploads\/event/gi,
    '$1https://portal.huttonsgroup.com/uploads/event',
  );
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

interface EventRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  /** TransformationEventCategory value to filter events by (e.g. 'Super Picker', 'Super Calculator'). */
  category?: string;
  projectId?: string;
  /** Dialog title; defaults to 'Super Picker Training and Registration' for back-compat. */
  title?: string;
  /** Empty-state message when no events match. */
  emptyMessage?: string;
  ceaNumber?: string | null;
  initialTab?: 'all' | 'registered';
  /**
   * If provided, the dialog will auto-open the inner Confirm Registration /
   * Training Details dialog for the matching event once events have loaded.
   * Used by deep-links from emails (e.g. ?redirect=training&eventId=XXX).
   */
  initialEventId?: string | null;
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sValid = !Number.isNaN(s.getTime());
  const eValid = !Number.isNaN(e.getTime());
  if (!sValid && !eValid) return '-';
  const fmt = (d: Date) =>
    d.toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  if (sValid && eValid) return `${fmt(s)} — ${fmt(e)}`;
  return fmt(sValid ? s : e);
}

const EventRegistrationDialog: React.FC<EventRegistrationDialogProps> = ({
  open,
  onClose,
  category,
  projectId,
  title = 'Super Picker Training and Registration',
  emptyMessage,
  ceaNumber,
  initialTab = 'all',
  initialEventId = null,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<RegistrationEventSummary[]>([]);
  const [registeredEvents, setRegisteredEvents] = useState<RegistrationEventSummary[]>([]);
  const [pendingEvent, setPendingEvent] = useState<RegistrationEventSummary | null>(null);
  const [detailsTab, setDetailsTab] = useState<'overview' | 'reviews'>('overview');
  const [selectedReview, setSelectedReview] = useState<TrainingReviewSummary | null>(null);
  const [registering, setRegistering] = useState<boolean>(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);
  const [externalRegisterFailure, setExternalRegisterFailure] = useState<string | null>(null);
  const [ecbInvites, setEcbInvites] = useState<EcbInviteRow[]>([
    { name: '', agency: '', mobile: '' },
  ]);
  const [myEcbInvites, setMyEcbInvites] = useState<TrainingECBInviteSummary[]>([]);
  const [myEcbInviteRows, setMyEcbInviteRows] = useState<EcbInviteRow[]>([]);
  const [myEcbInvitesLoading, setMyEcbInvitesLoading] = useState<boolean>(false);
  const [myEcbInvitesError, setMyEcbInvitesError] = useState<string | null>(null);
  const [myEcbInvitesSuccess, setMyEcbInvitesSuccess] = useState<string | null>(null);
  const [myEcbInvitesSaving, setMyEcbInvitesSaving] = useState<boolean>(false);
  const [reviewEvent, setReviewEvent] = useState<RegistrationEventSummary | null>(null);
  const [reviewText, setReviewText] = useState<string>('');
  const [learningPoints, setLearningPoints] = useState<string>('');
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSubmitting, setReviewSubmitting] = useState<boolean>(false);
  const [showTrainingReviewCelebration, setShowTrainingReviewCelebration] = useState<boolean>(false);
  const [showPastRegisteredTrainings, setShowPastRegisteredTrainings] = useState<boolean>(true);
  const [showUpcomingRegisteredTrainings, setShowUpcomingRegisteredTrainings] = useState<boolean>(true);
  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(new Date()));
  const [activeTab, setActiveTab] = useState<'all' | 'registered'>(initialTab);
  const [hasInitializedViewMonth, setHasInitializedViewMonth] = useState<boolean>(false);
  // When true, fetch every TransformationTraining event regardless of
  // category. Useful for users who want to see training outside their primary
  // app (e.g. a Super Picker user viewing Super Calculator sessions).
  // Checked by default so users see the full training calendar out of the box.
  const [showOthers, setShowOthers] = useState<boolean>(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const todayListRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusTodayRef = useRef<boolean>(false);
  const viewModeInitializedForOpenRef = useRef<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.getRegistrationEvents(
        projectId ? undefined : (showOthers ? 'all' : category),
        projectId,
      );
      const registeredList = await api.getRegisteredTrainingEvents(
        projectId ? undefined : (showOthers ? 'all' : category),
        projectId,
      );
      setEvents(list);
      setRegisteredEvents(registeredList);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [category, projectId, showOthers]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setEvents([]);
      setRegisteredEvents([]);
      setHasInitializedViewMonth(false);
      setMyEcbInvites([]);
      setMyEcbInviteRows([]);
      setMyEcbInvitesLoading(false);
      setMyEcbInvitesError(null);
      setMyEcbInvitesSuccess(null);
      setMyEcbInvitesSaving(false);
      setReviewEvent(null);
      setReviewText('');
      setLearningPoints('');
      setReviewError(null);
      setReviewSubmitting(false);
      setShowTrainingReviewCelebration(false);
      setShowPastRegisteredTrainings(true);
      setShowUpcomingRegisteredTrainings(true);
      setViewMonth(startOfMonth(new Date()));
      setShowOthers(true);
      viewModeInitializedForOpenRef.current = false;
      return;
    }
    setActiveTab(initialTab);
    // Apply the default view once per open so mid-session resize doesn't
    // yank the user between calendar and list.
    if (!viewModeInitializedForOpenRef.current) {
      viewModeInitializedForOpenRef.current = true;
      setViewMode(isMobile ? 'list' : 'calendar');
      setViewMonth(startOfMonth(new Date()));
      if (isMobile) {
        shouldFocusTodayRef.current = true;
      }
    }
  }, [open, initialTab, isMobile]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  // Auto-open the inner Confirm Registration / Training Details dialog when
  // the dialog is launched with `initialEventId` (e.g. via an EDM deep-link
  // such as `?redirect=training&eventId=ABC12345`). Runs once per open after
  // events have loaded; we look in both the unregistered and registered
  // event lists so the link works regardless of registration state.
  //
  // If the event isn't found in the current category-filtered list, we
  // automatically toggle "Show Other Trainings" on so the broader fetch can
  // surface events outside the user's primary app category. Doesn't apply
  // when a projectId is set (those are project-scoped lists).
  const initialEventConsumedRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initialEventConsumedRef.current = null;
      return;
    }
    if (!initialEventId) return;
    if (initialEventConsumedRef.current === initialEventId) return;
    if (loading) return;
    const match =
      events.find((e) => e.id === initialEventId) ||
      registeredEvents.find((e) => e.id === initialEventId);
    if (!match) {
      // Event not visible under the current filter. If we're filtering by
      // category (no projectId) and haven't already broadened the search,
      // flip "Show Others" on and let the reload bring it in.
      if (!projectId && !showOthers) {
        setShowOthers(true);
      }
      return;
    }
    initialEventConsumedRef.current = initialEventId;
    setRegisterError(null);
    setRegisterSuccess(null);
    setEcbInvites([{ name: '', agency: '', mobile: '' }]);
    setDetailsTab('overview');
    setSelectedReview(null);
    setPendingEvent(match);
  }, [open, initialEventId, loading, events, registeredEvents, projectId, showOthers]);

  useEffect(() => {
    let cancelled = false;
    const shouldLoad =
      open &&
      pendingEvent?.registered &&
      (pendingEvent.assignedCategories || []).includes(RECRUITMENT_CATEGORY);

    if (!shouldLoad || !pendingEvent) {
      setMyEcbInvites([]);
      setMyEcbInviteRows([]);
      setMyEcbInvitesLoading(false);
      setMyEcbInvitesError(null);
      setMyEcbInvitesSuccess(null);
      return;
    }

    setMyEcbInvitesLoading(true);
    setMyEcbInvitesError(null);
    setMyEcbInvitesSuccess(null);
    api.getMyEventECBInvites(pendingEvent.id)
      .then((ecbs) => {
        if (!cancelled) {
          setMyEcbInvites(ecbs);
          setMyEcbInviteRows(ecbs.map((ecb) => ({
            name: ecb.name || '',
            agency: ecb.agency || '',
            mobile: ecb.mobile || '',
          })));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setMyEcbInvites([]);
          setMyEcbInviteRows([]);
          setMyEcbInvitesError(e instanceof Error ? e.message : 'Failed to load ECBs');
        }
      })
      .finally(() => {
        if (!cancelled) setMyEcbInvitesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, pendingEvent]);

  // When events are loaded, snap the calendar to the month containing the
  // earliest upcoming event (or current month if none).
  useEffect(() => {
    if (!open || hasInitializedViewMonth || events.length === 0) return;
    const earliest = events.reduce<Date | null>((acc, ev) => {
      const d = new Date(ev.startDate);
      if (Number.isNaN(d.getTime())) return acc;
      return acc === null || d < acc ? d : acc;
    }, null);
    if (earliest) setViewMonth(startOfMonth(new Date()));
    setHasInitializedViewMonth(true);
  }, [open, events, hasInitializedViewMonth]);

  // Map date key -> events occurring on that date (covers multi-day events).
  const eventsByDay = useMemo(() => {
    const map = new Map<string, RegistrationEventSummary[]>();
    for (const ev of events) {
      const s = new Date(ev.startDate);
      const e = new Date(ev.endDate);
      if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) continue;
      const cur = startOfDay(s);
      const end = startOfDay(e);
      while (cur <= end) {
        const key = dateKey(cur);
        const list = map.get(key) || [];
        list.push(ev);
        map.set(key, list);
        cur.setDate(cur.getDate() + 1);
      }
    }
    return map;
  }, [events]);

  // Build a 6x7 calendar grid for viewMonth (Sunday-start).
  const calendarCells = useMemo(() => {
    const firstDow = viewMonth.getDay(); // 0 = Sun
    const gridStart = new Date(viewMonth);
    gridStart.setDate(gridStart.getDate() - firstDow);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      cells.push(d);
    }
    return cells;
  }, [viewMonth]);

  // Agenda rows for list view: days in the selected month that have events.
  // When viewing the current month, always include today so list focus can
  // land on the current date even if nothing is scheduled.
  const listDaysInMonth = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const days: { date: Date; events: RegistrationEventSummary[] }[] = [];
    eventsByDay.forEach((dayEvents, key) => {
      const [y, m, d] = key.split('-').map(Number);
      if (y === year && m === month + 1) {
        days.push({ date: new Date(y, m - 1, d), events: dayEvents });
      }
    });
    const today = new Date();
    if (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      !days.some(({ date }) => sameDay(date, today))
    ) {
      days.push({ date: startOfDay(today), events: [] });
    }
    days.sort((a, b) => a.date.getTime() - b.date.getTime());
    return days;
  }, [eventsByDay, viewMonth]);

  // After switching to list view (or opening on mobile), scroll to today's
  // day group once the list has painted.
  useEffect(() => {
    if (viewMode !== 'list' || activeTab !== 'all' || !shouldFocusTodayRef.current) return;
    if (loading) return;
    const timer = window.setTimeout(() => {
      shouldFocusTodayRef.current = false;
      todayListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [viewMode, activeTab, viewMonth, loading, listDaysInMonth]);

  const handleViewModeChange = (_: React.MouseEvent<HTMLElement>, next: 'calendar' | 'list' | null) => {
    if (!next) return;
    if (next === 'list') {
      setViewMonth(startOfMonth(new Date()));
      shouldFocusTodayRef.current = true;
    }
    setViewMode(next);
  };

  const filteredRegisteredEvents = useMemo(() => {
    const now = new Date();
    return registeredEvents
      .filter((ev) => {
        const endDate = new Date(ev.endDate);
        const isPastTraining = !Number.isNaN(endDate.getTime()) && now > endDate;
        if (isPastTraining) return showPastRegisteredTrainings;
        return showUpcomingRegisteredTrainings;
      })
      .sort((a, b) => {
        const aEnd = new Date(a.endDate).getTime();
        const bEnd = new Date(b.endDate).getTime();
        return (Number.isNaN(bEnd) ? 0 : bEnd) - (Number.isNaN(aEnd) ? 0 : aEnd);
      });
  }, [registeredEvents, showPastRegisteredTrainings, showUpcomingRegisteredTrainings]);

  const pendingEventIsPast = useMemo(() => {
    if (!pendingEvent) return false;
    const endDate = new Date(pendingEvent.endDate);
    return !Number.isNaN(endDate.getTime()) && new Date() > endDate;
  }, [pendingEvent]);

  // True when "now" falls within the training's start/end window. While the
  // training is live, the dialog flips into a "Mark attendance" state instead
  // of showing the Confirm-registration / read-only-details actions.
  const pendingEventIsInProgress = useMemo(() => {
    if (!pendingEvent) return false;
    const startDate = new Date(pendingEvent.startDate);
    const endDate = new Date(pendingEvent.endDate);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;
    const now = new Date();
    return now >= startDate && now <= endDate;
  }, [pendingEvent]);

  // Events flagged with `Event.RegistrationRequired = 0` don't have a
  // Confirm-Registration step. The dialog shows the Training Details view
  // directly; users can still mark attendance while the session is live.
  // `null`/`undefined` (legacy data) is treated as "registration required".
  const pendingEventSkipsRegistration =
    pendingEvent?.registrationRequired === false;

  const handleRegister = (event: RegistrationEventSummary) => {
    setRegisterError(null);
    setRegisterSuccess(null);
    setEcbInvites([{ name: '', agency: '', mobile: '' }]);
    setDetailsTab('overview');
    setSelectedReview(null);
    setPendingEvent(event);
  };

  const handleCancelConfirm = () => {
    if (registering) return;
    setPendingEvent(null);
    setSelectedReview(null);
    setRegisterError(null);
    setEcbInvites([{ name: '', agency: '', mobile: '' }]);
    setMyEcbInvites([]);
    setMyEcbInviteRows([]);
    setMyEcbInvitesError(null);
    setMyEcbInvitesSuccess(null);
  };

  const handleConfirmRegister = async () => {
    if (!pendingEvent) return;
    logConfirmTrainingRegistrationClick(pendingEvent.id, ceaNumber);
    const isRecruitment = (pendingEvent.assignedCategories || []).includes(RECRUITMENT_CATEGORY);
    const invites = ecbInvites
      .map((invite) => ({
        name: invite.name.trim(),
        agency: invite.agency.trim(),
        mobile: invite.mobile.trim(),
      }))
      .filter((invite) => invite.name || invite.agency || invite.mobile);
    if (isRecruitment && invites.some((invite) => !invite.name || !invite.agency)) {
      setRegisterError('Name and agency are required for each invited ECB.');
      return;
    }
    setRegistering(true);
    setRegisterError(null);
    try {
      await api.registerForEvent(pendingEvent.id, invites);
      setRegisterSuccess(`You have been registered for ${pendingEvent.name}.`);
      // Optimistically mark this event as registered in local state so the
      // Register button is hidden/disabled immediately, without having to
      // wait for the upcoming/registered lists to refetch. `load()` below
      // will reconcile against the server.
      const registeredEventId = pendingEvent.id;
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === registeredEventId ? { ...ev, registered: true } : ev,
        ),
      );
      setRegisteredEvents((prev) =>
        prev.some((ev) => ev.id === registeredEventId)
          ? prev
          : [...prev, { ...pendingEvent, registered: true }],
      );
      void load();
      setPendingEvent(null);
      setEcbInvites([{ name: '', agency: '', mobile: '' }]);
    } catch (e) {
      const code =
        e && typeof e === 'object' && 'code' in (e as Record<string, unknown>)
          ? ((e as { code?: unknown }).code as string | undefined)
          : undefined;
      if (code === 'external_registration_failed') {
        setExternalRegisterFailure(
          e instanceof Error ? e.message : 'External training registration failed',
        );
      } else {
        setRegisterError(e instanceof Error ? e.message : 'Failed to register');
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleSaveMyEcbInvites = async () => {
    if (!pendingEvent) return;
    const invites = myEcbInviteRows
      .map((invite) => ({
        name: invite.name.trim(),
        agency: invite.agency.trim(),
        mobile: invite.mobile.trim(),
      }))
      .filter((invite) => invite.name || invite.agency || invite.mobile);
    if (invites.some((invite) => !invite.name || !invite.agency)) {
      setMyEcbInvitesError('Name and agency are required for each invited ECB.');
      return;
    }

    setMyEcbInvitesSaving(true);
    setMyEcbInvitesError(null);
    setMyEcbInvitesSuccess(null);
    try {
      const saved = await api.updateMyEventECBInvites(pendingEvent.id, invites);
      setMyEcbInvites(saved);
      setMyEcbInviteRows(saved.map((ecb) => ({
        name: ecb.name || '',
        agency: ecb.agency || '',
        mobile: ecb.mobile || '',
      })));
      setMyEcbInvitesSuccess('ECBs have been saved successfully.');
    } catch (e) {
      setMyEcbInvitesError(e instanceof Error ? e.message : 'Failed to update ECBs');
    } finally {
      setMyEcbInvitesSaving(false);
    }
  };

  const handleOpenReviewDialog = (event: RegistrationEventSummary) => {
    setReviewEvent(event);
    setReviewText(event.review || '');
    setLearningPoints(event.learningPoints || '');
    setReviewError(null);
  };

  const handleCloseReviewDialog = () => {
    if (reviewSubmitting) return;
    setReviewEvent(null);
    setReviewText('');
    setLearningPoints('');
    setReviewError(null);
  };

  const handleSubmitReview = async () => {
    if (!reviewEvent) return;
    const trimmedReview = reviewText.trim();
    const trimmedLearningPoints = learningPoints.trim();
    if (!trimmedReview && !trimmedLearningPoints) {
      setReviewError('Please enter a review or learning point before submitting.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      await api.submitTrainingReview(reviewEvent.id, trimmedReview, trimmedLearningPoints);
      setRegisterSuccess(`Your review for ${reviewEvent.name} has been submitted.`);
      setReviewEvent(null);
      setReviewText('');
      setLearningPoints('');
      setShowTrainingReviewCelebration(true);
      void load();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--color-surface)',
          color: 'var(--color-text-strong)',
          minHeight: '60vh',
          // Wider than MUI `md` on tablet/desktop so the calendar grid
          // and list agenda have more room; mobile stays full-bleed.
          width: { xs: '100%', sm: '92vw', md: '90vw' },
          maxWidth: { xs: '100%', sm: 820, md: 1100, lg: 1280 },
        },
      }}
    >
      {showTrainingReviewCelebration && (
        <TrainingReviewCelebrationOverlay onDone={() => setShowTrainingReviewCelebration(false)} />
      )}
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: 'flex',
            // On mobile the checkbox label was wrapping to 3 lines next
            // to the tabs because both fit in a single row. Stack them
            // vertically below `sm` and let the tabs scroll horizontally
            // so neither element has to compete for horizontal space.
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 0.5, sm: 2 },
            mt: 0.5,
          }}
        >
          {!projectId ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showOthers}
                  disabled={loading}
                  onChange={(e) => setShowOthers(e.target.checked)}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: 'var(--color-text)', whiteSpace: 'nowrap' }}
                >
                  Show Other Trainings
                </Typography>
              }
              sx={{ ml: -0.5, flexShrink: 0 }}
            />
          ) : (
            <Box />
          )}
          <Tabs
            value={activeTab}
            onChange={(_, nextTab: 'all' | 'registered') => setActiveTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 36,
              // Allow the tab strip to consume the full row on mobile so
              // both tabs are reachable via swipe / scroll buttons.
              maxWidth: '100%',
              '& .MuiTabs-flexContainer': { gap: { xs: 0.5, sm: 1 } },
            }}
          >
            <Tab
              value="all"
              label="View all trainings"
              sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600, px: { xs: 1, sm: 2 } }}
            />
            <Tab
              value="registered"
              label="My Registered Trainings"
              sx={{ minHeight: 36, textTransform: 'none', fontWeight: 600, px: { xs: 1, sm: 2 } }}
            />
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {registerSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRegisterSuccess(null)}>
            {registerSuccess}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : events.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
              border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
              borderRadius: 1,
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage || 'No events available for registration.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {activeTab === 'all' ? (
              <>
            {/* Month navigation + calendar/list toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => setViewMonth((m) => addMonths(m, -1))}
                  aria-label="Previous month"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: { xs: 140, sm: 180 }, textAlign: 'center' }}>
                  {viewMonth.toLocaleString('en-SG', { month: 'long', year: 'numeric' })}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setViewMonth((m) => addMonths(m, 1))}
                  aria-label="Next month"
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="View mode"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    px: { xs: 1, sm: 1.5 },
                    py: 0.35,
                  },
                }}
              >
                <ToggleButton value="calendar" aria-label="Calendar view">
                  <CalendarMonthIcon fontSize="small" sx={{ mr: 0.5 }} />
                  Calendar
                </ToggleButton>
                <ToggleButton value="list" aria-label="List view">
                  <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
                  List
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Legend — shared by calendar and list views */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2,
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: TRAINING_GREEN,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'var(--color-text-strong)' }}>
                  Available
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: REGISTERED_GREY,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: '0.75rem', color: '#fff' }} />
                </Box>
                <Typography variant="caption" sx={{ color: 'var(--color-text-strong)' }}>
                  Already registered
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    bgcolor: PAST_EVENT_RED,
                  }}
                />
                <Typography variant="caption" sx={{ color: 'var(--color-text-strong)' }}>
                  Past event
                </Typography>
              </Box>
            </Box>

            {viewMode === 'calendar' ? (
              <>
            {/* Weekday header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 0.5,
                mb: 0.5,
              }}
            >
              {/* Weekday columns also need minmax(0,1fr) for consistent widths. */}
              {WEEKDAYS.map((w) => (
                <Typography
                  key={w}
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    color: 'var(--color-text-subtle)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {w}
                </Typography>
              ))}
            </Box>

            {/* Day grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gridAutoRows: 'minmax(92px, auto)',
                gap: 0.5,
              }}
            >
              {calendarCells.map((day) => {
                const inMonth = day.getMonth() === viewMonth.getMonth();
                const isToday = sameDay(day, new Date());
                const dayEvents = eventsByDay.get(dateKey(day)) || [];
                return (
                  <Box
                    key={day.toISOString()}
                    sx={{
                      border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                      borderRadius: 1,
                      p: 0.5,
                      minHeight: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      bgcolor: inMonth
                        ? 'rgba(var(--color-text-rgb), 0.02)'
                        : 'transparent',
                      opacity: inMonth ? 1 : 0.4,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isToday ? 700 : 500,
                        color: isToday ? TRAINING_GREEN : 'var(--color-text-strong)',
                        alignSelf: 'flex-end',
                        px: 0.5,
                      }}
                    >
                      {day.getDate()}
                    </Typography>
                    {dayEvents.map((ev) => {
                      const isRegistered = ev.registered;
                      const endDate = new Date(ev.endDate);
                      const isPastEvent = !Number.isNaN(endDate.getTime()) && new Date() > endDate;
                      const eventColor = isPastEvent ? PAST_EVENT_RED : isRegistered ? REGISTERED_GREY : TRAINING_GREEN;
                      const eventHoverColor = isPastEvent ? PAST_EVENT_RED_HOVER : isRegistered ? REGISTERED_GREY : TRAINING_GREEN_HOVER;
                      return (
                        <Tooltip
                          key={`${ev.id}-${dateKey(day)}`}
                          title={
                            <Box sx={{ py: 0.25 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                }}
                              >
                                {ev.name}
                              </Typography>
                              <Box
                                sx={{
                                  borderTop: '1px solid rgba(255,255,255,0.25)',
                                  my: 0.5,
                                }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: '0.95rem' }} />
                                <Typography
                                  sx={{ fontSize: '0.8rem', fontWeight: 700 }}
                                >
                                  {formatDateRange(ev.startDate, ev.endDate)}
                                </Typography>
                              </Box>
                              {ev.venue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                  <LocationOnIcon sx={{ fontSize: '0.95rem' }} />
                                  <Typography sx={{ fontSize: '0.8rem' }}>
                                    {ev.venue}
                                  </Typography>
                                </Box>
                              )}
                              {isRegistered && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <CheckCircleIcon sx={{ fontSize: '0.95rem' }} />
                                  <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                    Already registered
                                  </Typography>
                                </Box>
                              )}
                              {isPastEvent && (
                                <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic', mt: 0.5 }}>
                                  Past event
                                </Typography>
                              )}
                            </Box>
                          }
                          arrow
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => handleRegister(ev)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRegister(ev);
                              }
                            }}
                            sx={{
                              bgcolor: eventColor,
                              color: '#fff',
                              borderRadius: 0.75,
                              px: 0.75,
                              py: 0.25,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                              maxWidth: '100%',
                              minWidth: 0,
                              overflow: 'hidden',
                              opacity: isRegistered ? 0.85 : 1,
                              '&:hover': {
                                bgcolor: eventHoverColor,
                                opacity: 1,
                              },
                              '&:focus-visible': {
                                outline: '2px solid',
                                outlineColor: eventHoverColor,
                                outlineOffset: 1,
                              },
                            }}
                          >
                            {isRegistered && (
                              <CheckCircleIcon
                                sx={{ fontSize: '0.85rem', flexShrink: 0 }}
                              />
                            )}
                            <Box
                              component="span"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {ev.name}
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {listDaysInMonth.length === 0 ||
                listDaysInMonth.every(({ events: dayEvents }) => dayEvents.length === 0) ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 200,
                      border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    {/* Keep a today anchor so list focus still works when the
                        month has no trainings at all. */}
                    <Box
                      ref={
                        viewMonth.getMonth() === new Date().getMonth() &&
                        viewMonth.getFullYear() === new Date().getFullYear()
                          ? todayListRef
                          : undefined
                      }
                      sx={{ scrollMarginTop: 16 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No trainings scheduled for{' '}
                        {viewMonth.toLocaleString('en-SG', { month: 'long', year: 'numeric' })}.
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  listDaysInMonth.map(({ date, events: dayEvents }) => {
                    const isToday = sameDay(date, new Date());
                    // Skip empty non-today placeholders (shouldn't appear, but
                    // today-with-no-events is shown so focus can land on it).
                    if (dayEvents.length === 0 && !isToday) return null;
                    return (
                      <Box
                        key={dateKey(date)}
                        ref={isToday ? todayListRef : undefined}
                        sx={{
                          border: isToday
                            ? `2px solid ${TRAINING_GREEN}`
                            : '1px solid rgba(var(--color-text-rgb), 0.12)',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          bgcolor: isToday
                            ? 'rgba(46, 125, 50, 0.06)'
                            : 'rgba(var(--color-text-rgb), 0.02)',
                          scrollMarginTop: 16,
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: isToday ? TRAINING_GREEN : 'var(--color-text-strong)',
                            }}
                          >
                            {date.toLocaleDateString('en-SG', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </Typography>
                          {isToday && (
                            <Chip
                              size="small"
                              label="Today"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                bgcolor: TRAINING_GREEN,
                                color: '#fff',
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.25 }}>
                          {dayEvents.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, py: 0.5 }}>
                              No trainings today.
                            </Typography>
                          ) : (
                            dayEvents.map((ev) => {
                              const isRegistered = ev.registered;
                              const endDate = new Date(ev.endDate);
                              const isPastEvent =
                                !Number.isNaN(endDate.getTime()) && new Date() > endDate;
                              const eventColor = isPastEvent
                                ? PAST_EVENT_RED
                                : isRegistered
                                  ? REGISTERED_GREY
                                  : TRAINING_GREEN;
                              const eventHoverColor = isPastEvent
                                ? PAST_EVENT_RED_HOVER
                                : isRegistered
                                  ? REGISTERED_GREY
                                  : TRAINING_GREEN_HOVER;
                              return (
                                <Box
                                  key={`${ev.id}-${dateKey(date)}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleRegister(ev)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleRegister(ev);
                                    }
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1.25,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    p: 1,
                                    borderLeft: `4px solid ${eventColor}`,
                                    bgcolor: 'var(--color-surface)',
                                    '&:hover': {
                                      bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                                      borderLeftColor: eventHoverColor,
                                    },
                                    '&:focus-visible': {
                                      outline: '2px solid',
                                      outlineColor: eventHoverColor,
                                      outlineOffset: 1,
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: 0.5,
                                      bgcolor: eventColor,
                                      mt: 0.6,
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
                                      >
                                        {ev.name}
                                      </Typography>
                                      {isRegistered && (
                                        <Chip
                                          size="small"
                                          icon={<CheckCircleIcon sx={{ fontSize: '0.85rem !important' }} />}
                                          label="Registered"
                                          sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            bgcolor: REGISTERED_GREY,
                                            color: '#fff',
                                            '& .MuiChip-icon': { color: '#fff' },
                                          }}
                                        />
                                      )}
                                      {isPastEvent && (
                                        <Chip
                                          size="small"
                                          label="Past"
                                          sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            bgcolor: PAST_EVENT_RED,
                                            color: '#fff',
                                          }}
                                        />
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.35 }}>
                                      <AccessTimeIcon sx={{ fontSize: '0.95rem', color: 'var(--color-text-subtle)' }} />
                                      <Typography variant="caption" sx={{ color: 'var(--color-text)' }}>
                                        {formatDateRange(ev.startDate, ev.endDate)}
                                      </Typography>
                                    </Box>
                                    {ev.venue && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                        <LocationOnIcon sx={{ fontSize: '0.95rem', color: 'var(--color-text-subtle)' }} />
                                        <Typography variant="caption" sx={{ color: 'var(--color-text)' }}>
                                          {ev.venue}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPastRegisteredTrainings}
                        onChange={(e) => setShowPastRegisteredTrainings(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show past trainings attended"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showUpcomingRegisteredTrainings}
                        onChange={(e) => setShowUpcomingRegisteredTrainings(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show upcoming trainings"
                  />
                </Box>
                {registeredEvents.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 220,
                      border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      You have not registered for any trainings.
                    </Typography>
                  </Box>
                ) : filteredRegisteredEvents.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 220,
                      border: '1px dashed rgba(var(--color-text-rgb), 0.15)',
                      borderRadius: 1,
                      p: 3,
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No registered trainings match the selected filters.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      border: '1px solid rgba(var(--color-text-rgb), 0.1)',
                      borderRadius: 1,
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Training name</TableCell>
                          <TableCell>Date and Time</TableCell>
                          <TableCell>Venue</TableCell>
                          <TableCell>Categories</TableCell>
                          <TableCell align="right">Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRegisteredEvents.map((ev) => {
                          const endDate = new Date(ev.endDate);
                          const isPastTraining =
                            !Number.isNaN(endDate.getTime()) && new Date() > endDate;
                          const hasReview = Boolean((ev.review || '').trim() || (ev.learningPoints || '').trim());
                          const reviewApproved = Boolean(ev.reviewDateApproved);
                          return (
                            <TableRow key={ev.id}>
                              <TableCell>{ev.name}</TableCell>
                              <TableCell>{formatDateRange(ev.startDate, ev.endDate)}</TableCell>
                              <TableCell>{ev.venue || '-'}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(ev.assignedCategories || []).length > 0 ? (
                                    (ev.assignedCategories || []).map((category) => (
                                      <Chip
                                        key={`${ev.id}-${category}`}
                                        size="small"
                                        label={formatCategoryLabel(category)}
                                        sx={{
                                          bgcolor: TRAINING_GREEN,
                                          color: '#fff',
                                          fontWeight: 600,
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      -
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleRegister(ev)}
                                  >
                                    View details
                                  </Button>
                                  {isPastTraining ? (
                                    reviewApproved ? (
                                      <Typography variant="caption" color="text.secondary">
                                        Review approved, no further changes allowed
                                      </Typography>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="inherit"
                                        onClick={() => handleOpenReviewDialog(ev)}
                                      >
                                        {hasReview ? 'Edit review' : 'Add review'}
                                      </Button>
                                    )
                                  ) : null}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <Dialog
        open={pendingEvent !== null}
        onClose={handleCancelConfirm}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <DialogTitle>
          {pendingEvent?.registered || pendingEventIsPast || pendingEventIsInProgress || pendingEventSkipsRegistration ? 'Training Details' : 'Confirm Registration'}
        </DialogTitle>
        <DialogContent>
          {!pendingEvent?.registered && !pendingEventIsPast && !pendingEventIsInProgress && !pendingEventSkipsRegistration && (
            <DialogContentText sx={{ color: 'var(--color-text-strong)' }}>
              Are you sure you want to register for{' '}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {pendingEvent?.name}
              </Box>
              ?
            </DialogContentText>
          )}
          {(pendingEvent?.registered || pendingEventIsPast || pendingEventIsInProgress || pendingEventSkipsRegistration) && (
            <Typography sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>
              {pendingEvent?.name}
            </Typography>
          )}
          {pendingEvent && pendingEventIsPast && (
            <Tabs
              value={detailsTab}
              onChange={(_, value) => setDetailsTab(value as 'overview' | 'reviews')}
              sx={{ mt: 1.5, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.12)' }}
            >
              <Tab value="overview" label="Overview" />
              <Tab
                value="reviews"
                label={`Reviews${(pendingEvent.reviews || []).length > 0 ? ` (${(pendingEvent.reviews || []).length})` : ''}`}
              />
            </Tabs>
          )}
          {pendingEvent && (
            <Box sx={{ mt: 1.5 }}>
              {(!pendingEventIsPast || detailsTab === 'overview') && (
              <>
              <Box
                sx={{
                  borderTop: '1px solid rgba(var(--color-text-rgb), 0.15)',
                  mb: 1,
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AccessTimeIcon sx={{ fontSize: '1.05rem' }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>
                  {formatDateRange(pendingEvent.startDate, pendingEvent.endDate)}
                </Typography>
              </Box>
              {pendingEvent.venue && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '1.05rem' }} />
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    {pendingEvent.venue}
                  </Typography>
                </Box>
              )}
              {pendingEvent.description && (
                <Box
                  sx={{
                    mt: 1,
                    color: 'var(--color-text)',
                    fontSize: '0.875rem',
                    lineHeight: 1.55,
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: 1,
                    },
                    '& p': {
                      mt: 0,
                      mb: 1,
                    },
                    '& p:last-child': {
                      mb: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: normalizeEventDescriptionHtml(pendingEvent.description),
                  }}
                />
              )}
              {(pendingEvent.assignedCategories || []).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
                  {(pendingEvent.assignedCategories || []).map((category) => (
                    <Chip
                      key={`category-${category}`}
                      size="small"
                      label={formatCategoryLabel(category)}
                      sx={{
                        bgcolor: TRAINING_GREEN,
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              )}
              {(pendingEvent.assignedProjects || []).length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                  {(pendingEvent.assignedProjects || []).map((project) => (
                    <Chip
                      key={`project-${project.projectId}`}
                      size="small"
                      label={project.projectName || project.projectId}
                      sx={{
                        bgcolor: PROJECT_CHIP_BLUE,
                        color: '#fff',
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Box>
              )}
              {pendingEventIsPast ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: PAST_EVENT_RED }} />
                  <Typography sx={{ fontSize: '0.9rem', color: 'var(--color-text)' }}>
                    This is a past training event.
                  </Typography>
                </Box>
              ) : pendingEvent.registered ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: '1.05rem', color: REGISTERED_GREY }} />
                  <Typography sx={{ fontSize: '0.9rem', color: 'var(--color-text)' }}>
                    You are already registered for this training.
                  </Typography>
                </Box>
              ) : pendingEventSkipsRegistration ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: '1.05rem', color: TRAINING_GREEN }} />
                  <Typography sx={{ fontSize: '0.9rem', color: 'var(--color-text)' }}>
                    No registration required — just turn up. You can mark your
                    attendance once the session starts.
                  </Typography>
                </Box>
              ) : (
                null
              )}
              </>
              )}
              {pendingEventIsPast && detailsTab === 'reviews' && (() => {
                const reviews = pendingEvent.reviews || [];
                const reviewerInitial = (name: string | null, ceaNumber: string) =>
                  ((name || ceaNumber || '?').trim().charAt(0) || '?').toUpperCase();
                return (
                  <Box sx={{ mt: 2.5 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.3 }}
                      >
                        What past attendees are saying
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--color-text)', opacity: 0.75 }}>
                        Reviews and learning points from CEAs who attended trainings like this. Look out for similar upcoming sessions!
                      </Typography>
                    </Box>
                    {reviews.length === 0 ? (
                      <Box
                        sx={{
                          border: '1px dashed rgba(var(--color-text-rgb), 0.2)',
                          borderRadius: 1.5,
                          p: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          No reviews shared yet for this training.
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.25,
                      }}
                    >
                        {reviews.map((review, idx) => {
                          const displayName = review.name || review.ceaNumber;
                          const hasReview = Boolean((review.review || '').trim());
                          const hasLearning = Boolean((review.learningPoints || '').trim());
                          return (
                            <Box
                              key={`${review.ceaNumber}-${idx}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedReview(review)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedReview(review);
                                }
                              }}
                              sx={{
                                position: 'relative',
                                bgcolor: 'rgba(46, 125, 50, 0.04)',
                                border: '1px solid rgba(46, 125, 50, 0.18)',
                                borderLeft: `4px solid ${TRAINING_GREEN}`,
                                borderRadius: 1.5,
                                p: 1.75,
                                pl: 2,
                                minWidth: 0,
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
                                '&:hover': {
                                  borderColor: 'rgba(46, 125, 50, 0.35)',
                                  boxShadow: '0 8px 24px rgba(46, 125, 50, 0.12)',
                                  transform: 'translateY(-1px)',
                                },
                                '&:focus-visible': {
                                  outline: `2px solid ${TRAINING_GREEN}`,
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    bgcolor: TRAINING_GREEN,
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '0.85rem',
                                    flexShrink: 0,
                                  }}
                                >
                                  {reviewerInitial(review.name, review.ceaNumber)}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.2 }}
                                    noWrap
                                  >
                                    {displayName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: 'var(--color-text)', opacity: 0.7 }}
                                  >
                                    Past attendee
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {hasReview && (
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                    <FormatQuoteIcon
                                      sx={{
                                        fontSize: '1.1rem',
                                        color: TRAINING_GREEN,
                                        transform: 'scaleX(-1)',
                                        mt: '2px',
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: 'var(--color-text-strong)',
                                        fontStyle: 'italic',
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {review.review}
                                    </Typography>
                                  </Box>
                                )}
                                {hasLearning && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1,
                                      bgcolor: 'rgba(255, 193, 7, 0.1)',
                                      border: '1px solid rgba(255, 193, 7, 0.3)',
                                      borderRadius: 1,
                                      p: 1,
                                    }}
                                  >
                                    <EmojiObjectsIcon
                                      sx={{ fontSize: '1.1rem', color: '#F9A825', mt: '2px', flexShrink: 0 }}
                                    />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 700,
                                          color: '#8C6D00',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.5,
                                          display: 'block',
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        Key learning
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: 'var(--color-text-strong)', lineHeight: 1.5 }}
                                      >
                                        {review.learningPoints}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })()}
              {pendingEvent.registered &&
                !pendingEventIsPast &&
                !pendingEventIsInProgress &&
                (pendingEvent.assignedCategories || []).includes(RECRUITMENT_CATEGORY) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Invited ECBs ({myEcbInvites.length})
                  </Typography>
                  {myEcbInvitesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      {myEcbInviteRows.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          No ECBs invited.
                        </Typography>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name *</TableCell>
                              <TableCell>Agency *</TableCell>
                              <TableCell>Phone Number</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {myEcbInviteRows.map((ecb, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={ecb.name}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setMyEcbInviteRows((prev) => prev.map((row, i) =>
                                        i === index ? { ...row, name: value } : row,
                                      ));
                                    }}
                                    required
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={ecb.agency}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setMyEcbInviteRows((prev) => prev.map((row, i) =>
                                        i === index ? { ...row, agency: value } : row,
                                      ));
                                    }}
                                    required
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    size="small"
                                    value={ecb.mobile}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      setMyEcbInviteRows((prev) => prev.map((row, i) =>
                                        i === index ? { ...row, mobile: value } : row,
                                      ));
                                    }}
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell sx={{ width: 72 }}>
                                  <Button
                                    size="small"
                                    color="inherit"
                                    onClick={() => setMyEcbInviteRows((prev) => prev.filter((_, i) => i !== index))}
                                    disabled={myEcbInvitesSaving}
                                  >
                                    Remove
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          onClick={() => setMyEcbInviteRows((prev) => [
                            ...prev,
                            { name: '', agency: '', mobile: '' },
                          ])}
                          disabled={myEcbInvitesSaving}
                        >
                          + Add ECB
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveMyEcbInvites}
                          disabled={myEcbInvitesSaving}
                        >
                          {myEcbInvitesSaving ? 'Saving…' : 'Save ECBs'}
                        </Button>
                      </Box>
                      {myEcbInvitesError && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {myEcbInvitesError}
                        </Alert>
                      )}
                      {myEcbInvitesSuccess && (
                        <Alert
                          severity="success"
                          sx={{ mt: 1 }}
                          onClose={() => setMyEcbInvitesSuccess(null)}
                        >
                          {myEcbInvitesSuccess}
                        </Alert>
                      )}
                    </>
                  )}
                </Box>
              )}
              {!pendingEvent.registered &&
                !pendingEventIsPast &&
                !pendingEventIsInProgress &&
                !pendingEventSkipsRegistration &&
                (pendingEvent.assignedCategories || []).includes(RECRUITMENT_CATEGORY) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Invite ECBs
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name *</TableCell>
                        <TableCell>Agency *</TableCell>
                        <TableCell>Mobile</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ecbInvites.map((invite, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <TextField
                              size="small"
                              value={invite.name}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEcbInvites((prev) => prev.map((row, i) =>
                                  i === index ? { ...row, name: value } : row,
                                ));
                              }}
                              required
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={invite.agency}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEcbInvites((prev) => prev.map((row, i) =>
                                  i === index ? { ...row, agency: value } : row,
                                ));
                              }}
                              required
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={invite.mobile}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEcbInvites((prev) => prev.map((row, i) =>
                                  i === index ? { ...row, mobile: value } : row,
                                ));
                              }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell sx={{ width: 72 }}>
                            <Button
                              size="small"
                              color="inherit"
                              disabled={ecbInvites.length === 1}
                              onClick={() => setEcbInvites((prev) => prev.filter((_, i) => i !== index))}
                            >
                              Remove
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => setEcbInvites((prev) => [
                      ...prev,
                      { name: '', agency: '', mobile: '' },
                    ])}
                  >
                    + Add ECB
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        {registerError && (
          <Alert severity="error" sx={{ mx: 3, mb: 1 }} onClose={() => setRegisterError(null)}>
            {registerError}
          </Alert>
        )}
        <DialogActions>
          <Button
            onClick={handleCancelConfirm}
            color="inherit"
            disabled={registering}
          >
            {pendingEvent?.registered || pendingEventIsPast || pendingEventIsInProgress || pendingEventSkipsRegistration ? 'Close' : 'Cancel'}
          </Button>
          {!pendingEvent?.registered && !pendingEventIsPast && !pendingEventIsInProgress && !pendingEventSkipsRegistration && (
            <Button
              onClick={handleConfirmRegister}
              variant="contained"
              disabled={registering}
              startIcon={
                registering ? (
                  <CircularProgress size={16} sx={{ color: '#fff' }} />
                ) : (
                  <HowToRegIcon />
                )
              }
              sx={{
                bgcolor: 'var(--color-accent)',
                '&:hover': { bgcolor: 'var(--color-accent-strong)' },
              }}
            >
              {registering ? 'Registering…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text)',
          },
        }}
      >
        <DialogTitle sx={{ bgcolor: 'var(--color-surface)', borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
              Training Story
            </Typography>
            <IconButton onClick={() => setSelectedReview(null)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '24px !important', pb: 3 }}>
          {selectedReview && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: TRAINING_GREEN,
                  fontSize: 20,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {(selectedReview.name || selectedReview.ceaNumber || '??').substring(0, 2).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    position: 'relative',
                    ml: 1.5,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -11,
                      top: 20,
                      width: 0,
                      height: 0,
                      borderTop: '10px solid transparent',
                      borderRight: `15px solid ${TRAINING_GREEN}`,
                      borderBottom: '10px solid transparent',
                      zIndex: 0,
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: -9,
                      top: 21,
                      width: 0,
                      height: 0,
                      borderTop: '9px solid transparent',
                      borderRight: '13px solid var(--color-surface)',
                      borderBottom: '9px solid transparent',
                      zIndex: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: 'var(--color-surface)',
                      border: `2px solid ${TRAINING_GREEN}`,
                      boxShadow: '0 0 0 1px rgba(46, 125, 50, 0.18), 0 6px 20px rgba(46, 125, 50, 0.18)',
                      borderRadius: 2,
                      p: 2.5,
                      position: 'relative',
                      zIndex: 2,
                    }}
                  >
                    {selectedReview.review && (
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'var(--color-text)',
                          fontStyle: 'italic',
                          lineHeight: 1.6,
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '1.2em', color: TRAINING_GREEN }}>
                          "
                        </Box>
                        {selectedReview.review}
                        <Box component="span" sx={{ fontSize: '1.2em', color: TRAINING_GREEN }}>
                          "
                        </Box>
                      </Typography>
                    )}
                    {selectedReview.review && selectedReview.learningPoints && (
                      <Box sx={{ my: 1.5, borderTop: '1px solid rgba(var(--color-text-rgb), 0.12)' }} />
                    )}
                    {selectedReview.learningPoints && (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <EmojiObjectsIcon sx={{ fontSize: '1.1rem', color: '#F9A825', mt: '2px', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: '#8C6D00',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              display: 'block',
                              lineHeight: 1.2,
                            }}
                          >
                            Key learning
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'var(--color-text)', lineHeight: 1.6 }}>
                            {selectedReview.learningPoints}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 2, ml: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
                    {selectedReview.name || 'Agent'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
                    CEA: {selectedReview.ceaNumber || '-'} | Phone: {selectedReview.mobile || '-'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
                    {pendingEvent?.name || 'Training review'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid rgba(var(--color-text-rgb), 0.1)' }}>
          <Button onClick={() => setSelectedReview(null)} sx={{ color: 'var(--color-text)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reviewEvent !== null}
        onClose={handleCloseReviewDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          },
        }}
      >
        <DialogTitle>
          {reviewEvent?.review || reviewEvent?.learningPoints ? 'Edit review' : 'Add review'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {reviewEvent?.name}
          </Typography>
          <TextField
            label="Review"
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 200 }}
            helperText={`${reviewText.length}/200`}
            sx={{ mt: 1.5 }}
          />
          <TextField
            label="Learning points / key takeaways"
            value={learningPoints}
            onChange={(e) => setLearningPoints(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            inputProps={{ maxLength: 200 }}
            helperText={`${learningPoints.length}/200`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        {reviewError && (
          <Alert severity="error" sx={{ mx: 3, mb: 1 }} onClose={() => setReviewError(null)}>
            {reviewError}
          </Alert>
        )}
        <DialogActions>
          <Button onClick={handleCloseReviewDialog} color="inherit" disabled={reviewSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={reviewSubmitting}
            startIcon={
              reviewSubmitting ? (
                <CircularProgress size={16} sx={{ color: '#fff' }} />
              ) : undefined
            }
            sx={{
              bgcolor: 'var(--color-accent)',
              '&:hover': { bgcolor: 'var(--color-accent-strong)' },
            }}
          >
            {reviewSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={externalRegisterFailure !== null}
        onClose={() => setExternalRegisterFailure(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Registration failed</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography sx={{ mb: 1.5 }}>
              We couldn't complete your registration with the training system.
              Your registration was <strong>not</strong> saved.
            </Typography>
            <Typography sx={{ mb: 1.5 }}>
              Please retry or contact{' '}
              <Link
                href="mailto:transformationoffice@huttonsgroup.com"
                underline="hover"
                sx={{ color: 'primary.main' }}
              >
                transformationoffice@huttonsgroup.com
              </Link>{' '}
              for assistance.
            </Typography>
            {externalRegisterFailure && (
              <Typography variant="caption" color="text.secondary">
                Details: {externalRegisterFailure}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternalRegisterFailure(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default EventRegistrationDialog;
